<template>
  <a-spin :spinning="loading">
    <div
      v-if="isExpired"
      class="expired h-screen bg-back-bg flex justify-center items-center overflow-hidden"
    >
      <EmptyView>
        <template #content>
          <div class="text-16 text-font-80 font-medium mb-[20px]">
            {{ $t('dui-bu-qi') }}，{{ $t('ci-huo-dong-yi-guo-qi') }}
          </div>
          <NuxtLink :to="homeRoute">
            {{ $t('da-kai-you-yan') }}
          </NuxtLink>
        </template>
      </EmptyView>
    </div>

    <div v-else ref="container" class="pull-new" :class="{ noScroll: showInvitation }">
      <Resource
        :src="mainBg"
        :transform-resource="transformImage"
        class="w-full"
        style="object-fit: inherit !important"
      />
      <InviteList :h5="isH5" v-if="showInvitation" />
      <div v-if="!loading" class="button-group">
        <div v-if="!isH5" class="group pc">
          <template v-if="session?.user_id">
            <div class="button" v-if="isCode && isNewUser" @click="goWorkspace">
              <div
                class="btn-text"
                :style="{
                  background: mainColor,
                }"
              >
                {{ $t('jin-ru-guan-wang-li-ji-ti-yan') }}
              </div>
            </div>
            <template v-else>
              <div class="button">
                <div class="btn-text" :style="{ background: myLinkColor }" @click="handleShowLink">
                  {{ $t('huo-qu-wo-de-yao-qing-lian-jie') }}
                </div>
              </div>
              <div class="button">
                <div
                  class="btn-text success"
                  :style="{ background: rewardColor }"
                  @click="handleOpenInvitation"
                >
                  {{
                    showInvitation
                      ? $t('cha-kan-huo-dong-xiang-qing')
                      : $t('cha-kan-wo-de-jiang-li')
                  }}
                </div>
              </div>
            </template>
          </template>
          <div v-else class="button">
            <div
              class="btn-text"
              @click="handleLogin"
              :style="{
                background: mainColor,
              }"
            >
              {{ $t('zhu-ce-deng-lu-ling-qu-hao-li') }}
            </div>
          </div>
        </div>
        <div v-else class="group mobile">
          <div class="button" v-if="!session?.user_id">
            <div
              class="btn-text"
              :style="{
                background: mainColor,
              }"
              @click="handleLogin"
            >
              {{ $t('zhu-ce-deng-lu-ling-qu-hao-li') }}
            </div>
          </div>
          <template v-else>
            <div class="button" v-if="isCode && isNewUser">
              <div
                class="btn-text"
                :style="{
                  background: mainColor,
                }"
                @click="handleCopy"
              >
                {{ $t('fu-zhi-lian-jie-qian-wang-pc-duan-cha-kan') }}
              </div>
            </div>
            <template v-else>
              <div class="button button-sm">
                <div
                  class="btn-text"
                  :style="{
                    background: myLinkColor,
                  }"
                  @click="handleInvite"
                >
                  {{ $t('li-ji-yao-qing') }}
                </div>
              </div>
              <div class="button button-sm">
                <div
                  class="btn-text"
                  :style="{
                    background: rewardColor,
                  }"
                  @click="handleOpenInvitation"
                >
                  {{ showInvitation ? $t('cha-kan-huo-dong') : $t('cha-kan-jiang-li') }}
                </div>
              </div>
            </template>
          </template>
        </div>
      </div>
    </div>
  </a-spin>
</template>
<script lang="ts" setup>
import { USER_STATE } from '@/constants';
import { getI18nService } from '@rendora/common/services/i18n';
import {
  deleteUserState,
  getInviteActivity,
  getInviteRecordInfo,
  getPullNewLink,
  getUserState,
} from '~/api';
import type { ActivityItem, InviteInfoItem } from '~/api/interface/marketing';
import { useLoginInviteModal } from '~/hooks/useModal/modals/registerInvite';
import InviteList from './inviteList.vue';

const isExpired = ref(false);
const route = useRoute();
const session = useSession();
const homeRoute = useHomeRoute();
const { t: $t } = getI18nService();
const showInvitation = ref(false);
const recordList = ref<InviteInfoItem[]>();
const activityDetail = ref<ActivityItem>();
const loading = ref(false);
const PRIMARY_COLOR = '#6962FF';
const container = ref<HTMLDivElement>();

const { isMobile } = useDevice();

const isNewUser = ref(false);

// 邀请码可以为空，所以明确类型
const inviteCode = ref<string>();

const isH5 = computed(() => {
  // query h5 用于管理后台预览
  return Boolean(isMobile || route.query.h5);
});

// 是否邀请端
const isCode = computed(() => route.query.code);

// 工具：根据当前状态（H5/PC、排行榜/落地、是否邀请端）从 activityDetail 里选取 url 或 color
function pickPageProp(field: 'url' | 'color'): string | undefined {
  const detail = activityDetail.value;
  if (!detail) return;

  const pcLanding = detail.pc_web_page?.landing;
  const pcLandingFallback = pcLanding?.[field];

  // 榜单页优先 reward
  if (showInvitation.value) {
    const reward = isH5.value ? detail.mobile_web_page?.reward : detail.pc_web_page?.reward;
    return reward?.[field] || pcLandingFallback;
  }

  if (isH5.value) {
    const page = isCode.value
      ? detail.mobile_web_page?.inviteLanding
      : detail.mobile_web_page?.landing;
    return page?.[field] || pcLandingFallback;
  }

  const page = isCode.value ? detail.pc_web_page?.inviteLanding : detail.pc_web_page?.landing;
  return page?.[field] || pcLandingFallback;
}

const mainBg = computed(() => pickPageProp('url') || '');
const mainColor = computed(() => pickPageProp('color') || PRIMARY_COLOR);
const myLinkColor = computed(() => {
  const page = isH5.value
    ? activityDetail.value?.mobile_web_page
    : activityDetail.value?.pc_web_page;
  const color = isCode.value ? page?.inviteLanding.color : page?.landing?.color;
  return color || PRIMARY_COLOR;
});
const rewardColor = computed(() =>
  isH5.value
    ? activityDetail.value?.mobile_web_page?.reward?.color || PRIMARY_COLOR
    : activityDetail.value?.pc_web_page?.reward?.color || PRIMARY_COLOR,
);

const handleLogin = () => {
  trackEvent(TRACK_EVENT.YOUYAN_PLATFORM_INVITE_LOGIN);
  goLogin({
    code: route.query.code,
    redirect: encodeURIComponent(location.href),
  });
};

const handleInvite = async () => {
  trackEvent(TRACK_EVENT.YOUYAN_PLATFORM_INVITE_LINK);
  try {
    await copyToClipboard(
      `${$t(
        'kuai-lai-he-wo-yi-qi-lai-you-yan-jin-hang-aigc-3d-shi-pin-chuang-zuo-ba-xian-zai-dian-ji-lian-jie-jin-hang-zhu-ce-yao-qing-shuang-fang-du-you-la-xin-hao-li-xiang-song-linkvalue',
      )}${location.origin}/pull-new?code=${inviteCode.value}`,
    );
    message.success($t('fu-zhi-cheng-gong'));
  } catch (err) {
    console.warn('[Pull New] handleInvite error', err);
    message.error($t('fu-zhi-cheng-gong'));
  }
};

const handleShowLink = () => {
  trackEvent(TRACK_EVENT.YOUYAN_PLATFORM_INVITE_LINK);
  useLoginInviteModal({
    title: $t('ni-de-zhuan-shu-yao-qing-lian-jie-yi-sheng-cheng'),
    type: 'link',
    code: inviteCode.value,
  });
};

const goWorkspace = () => {
  location.href = `${location.origin}/workspace`;
};

const handleOpenInvitation = () => {
  trackEvent(TRACK_EVENT.YOUYAN_PLATFORM_INVITE_RANK);
  showInvitation.value = !showInvitation.value;
  nextTick(() => {
    if (container.value) {
      container.value.scrollTop = 0;
    }
  });
};

const handleCopy = async () => {
  trackEvent(TRACK_EVENT.YOUYAN_PLATFORM_INVITE_LINK_COPY);
  try {
    await copyToClipboard(`${location.origin}/workspace`);
    message.success($t('fu-zhi-cheng-gong'));
  } catch (err) {
    console.warn('[Pull New] handleCopy error', err);
    message.error($t('wen-ben-fu-zhi-shi-bai'));
  }
};

// 获取邀请码信息
const handleGetLink = async () => {
  try {
    const res: any = await getPullNewLink();
    if (res?.code === 0 && res.data.code) {
      inviteCode.value = res.data.code;
    }
  } catch (error) {
    console.warn('[Pull New] handleGetLink error', error);
  }
};

const getInviteActivityInfo = async () => {
  try {
    loading.value = true;
    const res: any = await getInviteActivity({
      is_last_version: 1,
      many: 1,
    });

    loading.value = false;
    if (!res.code && res.data) {
      if (res.data.length && res.data[0].status) {
        activityDetail.value = {
          ...res.data[0],
          mobile_web_page: JSON.parse(res.data[0].mobile_web_page),
          pc_web_page: JSON.parse(res.data[0].pc_web_page),
        };
        // 获取落地页详细配置后需要处理邀请码/任务相关逻辑
        if (session.value?.user_id) {
          await handleGetLink();

          if (route.query.code) {
            // 校验是否是新用户并查询任务
            await checkNewUser();
            if (!isH5.value) {
              await checkUserMission();
            }
          }

          // 从消息通知过来直接进入榜单页
          if (route.query.award) {
            handleOpenInvitation();
          }
        }
      } else {
        isExpired.value = true;
      }
    }
  } catch (error) {
    console.warn('[Pull New] getInviteActivityInfo error', error);

    loading.value = false;
  }
};

getInviteActivityInfo();

// 确认是否是刚注册的新用户
const checkNewUser = async () => {
  const res = await getUserState();

  if (res.data?.new_user_activity?.new_user) {
    isNewUser.value = true;

    deleteUserState(USER_STATE.NEW_USER_ACTIVITY);
  }
};

// 判断当前用户是否作为被邀请人有未完成任务
const checkUserMission = async () => {
  const res = await getInviteRecordInfo({
    is_invitee: 0,
    many: true,
  });
  if (!res.code) {
    const { data } = res;
    recordList.value = res.data;

    // 刚注册用户自动弹窗
    if (data?.length === 1) {
      // 未读状态
      if (!data[0].accept_read) {
        // 被邀请人有奖励
        if (data[0].accept_total_credit_coins) {
          useLoginInviteModal({
            title: $t('zhu-ce-wan-cheng'),
            type: 'registerAward',
            list: recordList.value,
          });
        } else {
          useLoginInviteModal({
            title: $t('zhu-ce-wan-cheng'),
            type: 'register',
            list: recordList.value,
            code: inviteCode.value,
          });
          // contentRef.value?.open('register')
        }
      } else {
        useLoginInviteModal({
          title: $t('zhu-li-ren-wu-shang-wei-wan-cheng'),
          type: 'buy',
          list: recordList.value,
          code: inviteCode.value,
        });
        // contentRef.value?.open('buy')
      }
    } else if (inviteCode.value !== route.query.code) {
      // 不是自己分享的链接提示
      message.info(
        $t(
          'nin-yi-jing-shi-you-yan-ping-tai-yong-hu-kuai-lai-yao-qing-xin-ren-ling-qu-jiang-li-ba',
        ),
      );
    }
  }
};

onMounted(() => {
  trackEvent(TRACK_EVENT.ROUTE_INVITE, {
    source: isH5.value ? 1 : 0, // 0-网页 1-移动
  });
});
</script>
<style lang="scss" scoped>
.pull-new {
  @apply h-screen relative sm:px-[0px] overflow-y-scroll;
  :global(.widget-visible) {
    display: none !important;
  }

  &.noScroll {
    overflow-y: hidden;
  }
  & > img {
    object-fit: inherit !important;
  }

  .button-group {
    @apply fixed bottom-[20px] left-1/2 right-0 w-full flex justify-center -translate-x-1/2;
    .group {
      @apply flex flex-nowrap gap-x-[20px];

      .button {
        @apply w-[350px] h-[68px] bg-[#ffffff] p-[12px] rounded-[16px] cursor-pointer;
        box-shadow: 0px 2px 20px 0px rgba(33, 33, 40, 0.2);
        .btn-text {
          @apply bg-primary text-16 text-white font-bold text-center w-full rounded-[8px] py-[11px] select-none hover:opacity-90 transition-colors;
        }
      }
      .button-sm {
        width: 175px;
      }
    }

    // .pc {
    //   @apply sm:hidden;
    // }
    // .mobile {
    //   @apply hidden sm:block;
    // }
  }
}

.pc-wrapper {
  min-width: 1300px;
}

:global(body) {
  min-width: auto;
  min-height: auto;
}
</style>
